import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Truck,
  MapPin,
  Calendar,
  Home,
  Package,
  User,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEPS = [
  "move_type",
  "zip_codes",
  "date",
  "home_size",
  "extras",
  "contact",
  "thank_you",
];

const STEP_LABELS = {
  move_type: "Type",
  zip_codes: "Route",
  date: "Date",
  home_size: "Size",
  extras: "Extras",
  contact: "Contact",
  thank_you: "Done",
};

const stepTransition = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.28, ease: "easeInOut" },
};

const initialData = {
  move_type: "",
  from_zip: "",
  to_zip: "",
  move_date: "",
  home_size: "",
  extras: [],
  full_name: "",
  email: "",
  phone: "",
  notes: "",
};

export default function QuoteWizard({ open, onOpenChange, prefill = null, context = null }) {
  // prefill: partial { move_type, notes, ... } merged into initial data
  // context: optional label shown at top of wizard, e.g. "Moving out of New York"
  const buildInitial = () => ({ ...initialData, ...(prefill || {}) });
  // If wizard is opened with a prefill that already includes move_type, skip step 1
  const initialStep = prefill && prefill.move_type ? 1 : 0;

  const [stepIdx, setStepIdx] = useState(initialStep);
  const [data, setData] = useState(buildInitial());
  const [submitting, setSubmitting] = useState(false);

  // Re-sync when wizard re-opens with new prefill
  useEffect(() => {
    if (open) {
      setData(buildInitial());
      setStepIdx(prefill && prefill.move_type ? 1 : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill?.move_type, prefill?.notes]);

  const currentStep = STEPS[stepIdx];
  const progress = ((stepIdx + 1) / STEPS.length) * 100;

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const toggleExtra = (key) => {
    setData((d) => ({
      ...d,
      extras: d.extras.includes(key)
        ? d.extras.filter((x) => x !== key)
        : [...d.extras, key],
    }));
  };

  const canNext = () => {
    switch (currentStep) {
      case "move_type":
        return !!data.move_type;
      case "zip_codes":
        return /^\d{3,}$/.test(data.from_zip) && /^\d{3,}$/.test(data.to_zip);
      case "date":
        return !!data.move_date;
      case "home_size":
        return !!data.home_size;
      case "extras":
        return true;
      case "contact":
        return (
          data.full_name.trim().length > 1 &&
          /^\S+@\S+\.\S+$/.test(data.email) &&
          data.phone.replace(/\D/g, "").length >= 7
        );
      default:
        return false;
    }
  };

  const next = async () => {
    if (currentStep === "contact") {
      await submit();
      return;
    }
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", data);
      setStepIdx(STEPS.indexOf("thank_you"));
      toast.success("Quote request sent. Movers will be in touch shortly.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStepIdx(prefill && prefill.move_type ? 1 : 0);
    setData(buildInitial());
  };

  const handleOpenChange = (next) => {
    if (!next) {
      onOpenChange(false);
      setTimeout(reset, 300);
    } else {
      onOpenChange(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 bg-white border-2 border-movewave-blue rounded-none shadow-[12px_12px_0_#002FA7] gap-0"
        data-testid="quote-wizard-dialog"
      >
        <DialogTitle className="sr-only">Get your free moving quote</DialogTitle>
        <DialogDescription className="sr-only">
          Multi-step form to request a moving quote from VanLines Scout.
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-movewave-ink bg-movewave-ink text-white">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-movewave-orange" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold">
              Step {Math.min(stepIdx + 1, STEPS.length - 1)} / {STEPS.length - 1}
              {" · "} {STEP_LABELS[currentStep]}
            </span>
          </div>
          <button
            onClick={() => handleOpenChange(false)}
            className="text-white/70 hover:text-white"
            data-testid="wizard-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {currentStep !== "thank_you" && (
          <div className="px-6 pt-4">
            <Progress value={progress} className="h-1 bg-gray-200" />
          </div>
        )}

        {context && currentStep !== "thank_you" && (
          <div className="mx-6 mt-4 px-4 py-2 bg-movewave-orange/10 border-2 border-movewave-orange flex items-center gap-2" data-testid="wizard-context-banner">
            <Sparkles className="w-4 h-4 text-movewave-orange flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-movewave-ink">
              {context}
            </span>
          </div>
        )}

        {/* Body */}
        <div className="px-6 sm:px-10 py-8 min-h-[360px]">
          <AnimatePresence mode="wait">
            <motion.div key={currentStep} {...stepTransition}>
              {currentStep === "move_type" && (
                <Step
                  title="What kind of move?"
                  subtitle="We'll match you with movers specialized in your move."
                  icon={<Truck className="w-5 h-5" />}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { id: "local", label: "Local move", hint: "Same city / under 50 mi" },
                      { id: "long_distance", label: "Long distance", hint: "Cross-state / over 50 mi" },
                    ].map((o) => (
                      <button
                        key={o.id}
                        onClick={() => set({ move_type: o.id })}
                        className={`text-left border-2 p-5 transition-all rounded-none ${
                          data.move_type === o.id
                            ? "border-movewave-blue bg-movewave-blue/5"
                            : "border-gray-200 hover:border-movewave-ink bg-white"
                        }`}
                        data-testid={`move-type-${o.id}`}
                      >
                        <div className="text-xl font-display font-bold">{o.label}</div>
                        <div className="text-sm text-gray-500 mt-1">{o.hint}</div>
                      </button>
                    ))}
                  </div>
                </Step>
              )}

              {currentStep === "zip_codes" && (
                <Step
                  title="Where are you moving from and to?"
                  subtitle="Just the ZIP codes — we'll handle the rest."
                  icon={<MapPin className="w-5 h-5" />}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <LabeledInput
                      label="From ZIP"
                      value={data.from_zip}
                      onChange={(v) => set({ from_zip: v.replace(/\D/g, "").slice(0, 6) })}
                      placeholder="10001"
                      testid="from-zip-input"
                      inputMode="numeric"
                    />
                    <LabeledInput
                      label="To ZIP"
                      value={data.to_zip}
                      onChange={(v) => set({ to_zip: v.replace(/\D/g, "").slice(0, 6) })}
                      placeholder="33101"
                      testid="to-zip-input"
                      inputMode="numeric"
                    />
                  </div>
                </Step>
              )}

              {currentStep === "date" && (
                <Step
                  title="When are you moving?"
                  subtitle="Approximate is fine — we'll confirm with you."
                  icon={<Calendar className="w-5 h-5" />}
                >
                  <LabeledInput
                    label="Move date"
                    type="date"
                    value={data.move_date}
                    onChange={(v) => set({ move_date: v })}
                    testid="move-date-input"
                  />
                </Step>
              )}

              {currentStep === "home_size" && (
                <Step
                  title="How big is your place?"
                  subtitle="This helps us estimate truck size and crew."
                  icon={<Home className="w-5 h-5" />}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { id: "studio", label: "Studio" },
                      { id: "1BR", label: "1 BR" },
                      { id: "2BR", label: "2 BR" },
                      { id: "3BR+", label: "3 BR+" },
                    ].map((o) => (
                      <button
                        key={o.id}
                        onClick={() => set({ home_size: o.id })}
                        className={`border-2 p-5 transition-all text-center rounded-none ${
                          data.home_size === o.id
                            ? "border-movewave-blue bg-movewave-blue/5 text-movewave-blue"
                            : "border-gray-200 hover:border-movewave-ink bg-white"
                        }`}
                        data-testid={`home-size-${o.id.toLowerCase().replace("+","-plus")}`}
                      >
                        <div className="text-lg font-display font-bold">{o.label}</div>
                      </button>
                    ))}
                  </div>
                </Step>
              )}

              {currentStep === "extras" && (
                <Step
                  title="Anything extra?"
                  subtitle="Optional — we'll include these in your quote."
                  icon={<Package className="w-5 h-5" />}
                >
                  <div className="space-y-3">
                    {[
                      { id: "packing", label: "Packing service", hint: "Pros pack everything for you" },
                      { id: "storage", label: "Storage", hint: "Short or long-term holding" },
                      { id: "supplies", label: "Boxes & supplies", hint: "Delivered before moving day" },
                      { id: "insurance", label: "Full-value insurance", hint: "Beyond standard coverage" },
                    ].map((o) => (
                      <label
                        key={o.id}
                        className={`flex items-center gap-4 border-2 p-4 cursor-pointer rounded-none ${
                          data.extras.includes(o.id)
                            ? "border-movewave-blue bg-movewave-blue/5"
                            : "border-gray-200 hover:border-movewave-ink"
                        }`}
                        data-testid={`extra-${o.id}`}
                      >
                        <Checkbox
                          checked={data.extras.includes(o.id)}
                          onCheckedChange={() => toggleExtra(o.id)}
                          className="rounded-none"
                        />
                        <div>
                          <div className="font-bold">{o.label}</div>
                          <div className="text-sm text-gray-500">{o.hint}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </Step>
              )}

              {currentStep === "contact" && (
                <Step
                  title="Last step — where do we send your quote?"
                  subtitle="Up to 4 vetted movers will reach out with offers."
                  icon={<User className="w-5 h-5" />}
                >
                  <div className="space-y-4">
                    <LabeledInput
                      label="Full name"
                      value={data.full_name}
                      onChange={(v) => set({ full_name: v })}
                      placeholder="Alex Morgan"
                      testid="full-name-input"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <LabeledInput
                        label="Email"
                        type="email"
                        value={data.email}
                        onChange={(v) => set({ email: v })}
                        placeholder="you@email.com"
                        testid="email-input"
                      />
                      <LabeledInput
                        label="Phone"
                        type="tel"
                        value={data.phone}
                        onChange={(v) => set({ phone: v })}
                        placeholder="(555) 123-4567"
                        testid="phone-input"
                      />
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      By submitting you agree to be contacted by partner moving companies.
                      No spam — only real quotes for your move.
                    </p>
                  </div>
                </Step>
              )}

              {currentStep === "thank_you" && (
                <div className="text-center py-8" data-testid="wizard-thank-you">
                  <div className="w-16 h-16 mx-auto bg-movewave-blue text-white flex items-center justify-center mb-6">
                    <Check className="w-8 h-8" strokeWidth={3} />
                  </div>
                  <h3 className="text-3xl font-display font-black tracking-tight mb-3">
                    You're all set.
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    We're matching you with vetted movers in your area. Expect a call
                    or email from up to 4 partners within the next few hours.
                  </p>
                  <Button
                    onClick={() => handleOpenChange(false)}
                    className="mt-8 bg-movewave-ink text-white font-bold uppercase tracking-wider rounded-none btn-brut h-12 px-8"
                    data-testid="wizard-done-btn"
                  >
                    Got it
                  </Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer / nav */}
        {currentStep !== "thank_you" && (
          <div className="flex items-center justify-between px-6 py-4 border-t-2 border-movewave-ink bg-movewave-paper">
            <button
              onClick={back}
              disabled={stepIdx === 0}
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-700 disabled:opacity-30"
              data-testid="wizard-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <Button
              onClick={next}
              disabled={!canNext() || submitting}
              className="bg-movewave-orange hover:bg-movewave-orange-hover text-white font-bold uppercase tracking-wider rounded-none h-12 px-6 btn-brut"
              data-testid="wizard-next-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {currentStep === "contact" ? "Get my quotes" : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Step({ title, subtitle, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">
        {icon} {title?.split(" ")[0]}
      </div>
      <h3 className="text-2xl sm:text-3xl font-display font-black tracking-tight leading-tight mb-2">
        {title}
      </h3>
      <p className="text-gray-600 mb-6">{subtitle}</p>
      {children}
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = "text", placeholder, testid, inputMode }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-gray-800 mb-2 block">
        {label}
      </span>
      <Input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-14 text-lg font-medium border-2 border-gray-200 focus:border-movewave-blue focus-visible:ring-0 rounded-none bg-white"
        data-testid={testid}
      />
    </label>
  );
}
